import type {
  AnalyticsEventDataMap,
  AnalyticsEventName,
  AnalyticsNavigationSource,
} from '~~/shared/analytics/events'
import {
  normalizeAnalyticsPath,
  sanitizeAnalyticsEventData,
  sanitizeUmamiPayload,
} from '~~/app/utils/analyticsPrivacy'

type TrackerTask = (tracker: UmamiTracker) => void

const trackerQueue: TrackerTask[] = []

let nextNavigationSource: AnalyticsNavigationSource = 'router'

export function setAnalyticsNavigationSource(source: AnalyticsNavigationSource) {
  nextNavigationSource = source
}

export function consumeAnalyticsNavigationSource(): AnalyticsNavigationSource {
  const source = nextNavigationSource
  nextNavigationSource = 'router'
  return source
}

let trackerTimer: number | undefined
let trackerAttempts = 0

function runtimeBoolean(value: boolean | string | null | undefined, fallback: boolean) {
  if (typeof value === 'boolean') return value

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()

    if (normalized === 'true') return true
    if (normalized === 'false') return false
  }

  return fallback
}

function stopTrackerPolling(clearQueue = false) {
  if (trackerTimer !== undefined) {
    window.clearInterval(trackerTimer)
    trackerTimer = undefined
  }

  trackerAttempts = 0

  if (clearQueue) trackerQueue.splice(0)
}

function flushTrackerQueue() {
  const tracker = window.umami

  if (!tracker) return false

  const tasks = trackerQueue.splice(0)

  for (const task of tasks) {
    try {
      task(tracker)
    } catch (error) {
      console.warn('[Morning Analytics] Une opération Analytics n’a pas pu être envoyée.', error)
    }
  }

  stopTrackerPolling()

  return true
}

function ensureTrackerPolling() {
  if (!import.meta.client || trackerTimer !== undefined) return

  if (flushTrackerQueue()) return

  trackerAttempts = 0

  trackerTimer = window.setInterval(() => {
    trackerAttempts += 1

    if (flushTrackerQueue()) return

    /*
     * Le tracker peut volontairement ne jamais devenir disponible :
     * bloqueur de contenu, DNT, incident réseau, etc.
     */
    if (trackerAttempts >= 100) {
      stopTrackerPolling(true)
    }
  }, 100)
}

function dispatchTrackerTask(task: TrackerTask) {
  if (!import.meta.client) return

  const tracker = window.umami

  if (tracker) {
    task(tracker)
    return
  }

  /*
   * Evite une croissance sans limite si le tracker est bloqué.
   */
  if (trackerQueue.length >= 100) trackerQueue.shift()

  trackerQueue.push(task)

  ensureTrackerPolling()
}

export function useAnalytics() {
  const runtimeConfig = useRuntimeConfig()

  const enabled = runtimeBoolean(runtimeConfig.public.analytics.umami.enabled, false)

  function trackPage(path?: string) {
    if (!enabled || !import.meta.client) return

    const normalizedPath = normalizeAnalyticsPath(path ?? window.location.pathname)

    dispatchTrackerTask((tracker) => {
      tracker.track((properties) => {
        const payload = sanitizeUmamiPayload(
          {
            ...properties,
            url: normalizedPath,
            title: normalizedPath,
          },
          window.location.origin,
        )

        return (
          payload || {
            ...properties,
            url: normalizedPath,
            title: normalizedPath,
          }
        )
      })
    })
  }

  function trackEvent<EventName extends AnalyticsEventName>(
    name: EventName,
    data: AnalyticsEventDataMap[EventName],
    path?: string,
  ) {
    if (!enabled || !import.meta.client) return

    const normalizedPath = normalizeAnalyticsPath(path ?? window.location.pathname)

    const safeData = sanitizeAnalyticsEventData(data as unknown as Record<string, unknown>)

    dispatchTrackerTask((tracker) => {
      tracker.track((properties) => {
        const payload = sanitizeUmamiPayload(
          {
            ...properties,
            url: normalizedPath,
            title: normalizedPath,
            name,
            data: safeData,
          },
          window.location.origin,
        )

        return (
          payload || {
            ...properties,
            url: normalizedPath,
            title: normalizedPath,
            name,
            data: safeData,
          }
        )
      })
    })
  }

  function setNavigationSource(source: AnalyticsNavigationSource) {
    setAnalyticsNavigationSource(source)
  }

  return {
    enabled,
    normalizePath: normalizeAnalyticsPath,
    setNavigationSource,
    trackPage,
    trackEvent,
  }
}
